<template>
  <v-container fluid class="pa-0 kits">
    <ERow>
      <ECol cols="12" class="py-0">
        <ReportTable
          name="kits"
          :provider="provider"
          :headers="filteredHeaders"
          :filter-fields="filterFields"
          sort-by="created_at"
          actions-portal-target="breadcrumbs-right"
        >
          <!-- Kit Name -->
          <template #item.name="{ item, header }">
            <NuxtLink :to="`/kits/${item.serial}`">
              <ETruncatedDiv :width="header.width">{{
                item.name
              }}</ETruncatedDiv>
            </NuxtLink>
          </template>

          <template #item.alarms="{ item }">
            <EReadMore
              v-if="getAlarms(item)"
              class="red--text text--darken-2"
              :content="getAlarms(item)"
              :max-length="2"
            />
            <div v-else class="green--text text--darken-2">Stable</div>
          </template>

          <template #item.nvrDeviceType="{ item }">
            {{ getSBCType(item) }}
          </template>

          <!-- Country Name -->
          <template #item.countryId="{ item }">
            <FlagIcon :code="getCountryCode(item)" />
            {{ item.countryName }}
          </template>

          <!-- Router Serial Number -->
          <template #item.routerSerial="{ item }">
            <span
              v-if="item.routerSerial"
              class="cursor-pointer primary--text"
              @click="openRouterSummary(item.routerSerial)"
            >
              {{ item.routerSerial }}
            </span>
          </template>

          <!-- Project name -->
          <template #item.projectName="{ item, header }">
            <nuxt-link
              :to="`/projects#project_dialog=true&project_exid=${item.projectExid}`"
            >
              <ETruncatedDiv :width="header.width">
                {{ item.projectName }}
              </ETruncatedDiv>
            </nuxt-link>
          </template>

          <!-- Created At -->
          <template #item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>

          <!-- Kit Status -->
          <template #item.status="{ item }">
            <span :class="getKitStatusColor(item.status)">
              {{ getKitStatusText(item.status) }}
            </span>
          </template>

          <!-- Tags -->
          <template #item.tags="{ item: kit }">
            <TagSelector
              class="kit-tag"
              :tags="tagStore.tags"
              :selected-tags="kit.tags"
              tooltip
              show-add-button
              @tag-click="(t) => onTagClick(t, kit)"
              @selected-tag-click="onSelectedTagClick"
            />
          </template>

          <!-- NVR Version -->
          <template #item.nvrVersion="{ item }">
            <span>
              {{ item.nvrVersion || "-" }}
            </span>
          </template>

          <!-- NVR Serial Number -->
          <template #item.nvrSerial="{ item }">
            <span v-if="item.nvrSerial">
              {{ item.nvrSerial || "-" }}
            </span>
          </template>

          <template #item.sims="{ item }">
            <SimsColumn
              :item="item"
              @open-sms-history-dialog="openSmsHistoryDialog"
            />
          </template>

          <!-- Nerves URL -->
          <template #item.nvrModel="{ item }">
            <span> {{ toTitleCase(item.nvrModel) || "-" }} </span>
            <OpenInNewWindowButton
              v-if="item.nvrNerves"
              hint="Open in Nerves"
              svg-icon="nerves"
              :target-link="getNervesDeviceUrl(item)"
              :icon-props="{
                width: '19',
                height: '11',
              }"
            />
            <span v-if="item.ups" style="color: green">UPS</span>
          </template>
        </ReportTable>
        <SmsHistoryDialog
          v-if="canViewSims"
          v-model="showSmsHistoryDialog"
          :sim="selectedSimNumber"
          :selected-sim="selectedSim"
          :read-only="!canSendSms"
        />
      </ECol>
    </ERow>
  </v-container>
</template>

<script lang="ts">
import KitsHeader from "@/components/kits/kitsHeaders"
import ReportTable from "@/components/ReportTable"
import filterFields from "@/components/kits/kitsSearchFilters"
import OpenInNewWindowButton from "@/components/OpenInNewWindowButton.vue"
import { AdminApi } from "@evercam/shared/api/adminApi"
import { mapStores } from "pinia"
import { useReportStore } from "@/stores/report"
import { useBreadcrumbStore } from "@/stores/breadcrumb"
import countries from "@evercam/shared/constants/countries"
import {
  KitAlarms,
  KitStatusesIdsClasses,
  NvrDeviceTypes,
} from "@/components/constants.js"
import { PERMISSIONS } from "@/constants/permissions"
import FlagIcon from "@evercam/shared/components/FlagIcon.vue"
import { useKitSummaryStore } from "@/stores/kitSummary"
import { useTagStore } from "~/stores/tag"
import TagSelector from "@/components/tags/TagSelector.vue"
import SimsColumn from "@/components/SimsColumn"
import SmsHistoryDialog from "@/components/sims/SmsHistoryDialog"
import { useRouterStore } from "@/stores/router"
import { Router } from "@evercam/shared/types/router"

export default {
  meta: {
    requiredPermissions: [PERMISSIONS.KITS.VIEW],
  },
  name: "Kits",
  components: {
    TagSelector,
    FlagIcon,
    OpenInNewWindowButton,
    ReportTable,
    SimsColumn,
    SmsHistoryDialog,
  },
  data: () => {
    return {
      headers: KitsHeader,
      countries: countries,
      loading: false,
      editedItem: {},
      filterFields,
      showSmsHistoryDialog: false,
      selectedSimNumber: "",
      selectedSim: null,
      router: {} as Router,
    }
  },
  head() {
    return {
      title: "Admin - Kits",
      meta: [
        { charset: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        {
          hid: "description",
          name: "description",
          content: "",
        },
      ],
    }
  },
  computed: {
    ...mapStores(
      useReportStore,
      useBreadcrumbStore,
      useTagStore,
      useRouterStore
    ),
    provider() {
      return AdminApi.kits.getAllKits
    },
    canEditKits() {
      return this.$permissions.user.can(PERMISSIONS.KITS.UPDATE)
    },
    canDeleteKits() {
      return this.$permissions.user.can(PERMISSIONS.KITS.DELETE)
    },
    filteredHeaders() {
      return this.headers.filter(
        (header) =>
          this.canEditKits || this.canDeleteKits || header.value !== "actions"
      )
    },
    canViewSims() {
      return (
        this.$permissions.user.can(PERMISSIONS.SIMS_AND_SMS.VIEW_SIMS) &&
        this.$permissions.user.can(PERMISSIONS.SIMS_AND_SMS.VIEW_SMS)
      )
    },
    canSendSms() {
      return this.$permissions.user.can(PERMISSIONS.SIMS_AND_SMS.SEND_SMS)
    },
  },
  watch: {
    "reportStore.searchFilters": {
      async handler(params) {
        const queryParams = this.toQueryString(params)
        useKitSummaryStore().previousKitsPageFullPath = queryParams
          ? `/kits?${queryParams}`
          : "/kits"
        this.initBreadcrumbs()
      },
      deep: true,
      immediate: true,
    },
  },
  async mounted() {
    await this.tagStore.fetchTags()
  },
  methods: {
    initBreadcrumbs() {
      this.breadcrumbStore.breadcrumbs = [
        {
          name: "Home",
          href: "/",
          icon: "fa-house",
        },
        {
          name: "Kits",
          icon: "fa-box",
        },
      ]
    },
    async onTagClick(tag, kit) {
      const isNewTag = !kit.tags.find((t) => t.id == tag.id)
      if (isNewTag) {
        await this.tagStore.tagKit({ kit, tag })
      } else {
        await this.tagStore.untagKit({ kit, tag })
      }
    },
    onSelectedTagClick(tag) {
      this.$root.$emit("toggle-tag", tag)
    },
    getAlarms(item) {
      if (!item?.alarms?.length) {
        return ""
      }

      return item?.alarms?.map(
        (alarm) => KitAlarms.find(({ value }) => value === alarm)?.name
      )
    },
    getKitStatusColor(status) {
      return KitStatusesIdsClasses[status]?.class || ""
    },
    getKitStatusText(status) {
      return KitStatusesIdsClasses[status]?.value || status
    },
    getNervesDeviceUrl(item) {
      return `${this.$config.public.nervesDashboardUrl}/devices/${item.nvrSerial}`
    },
    getCountryCode(item) {
      return countries.find((c) => c.name === item.countryName)?.value
    },
    getSBCType(item) {
      return NvrDeviceTypes.find((t) => t.value === item?.nvrDeviceType)?.name
    },
    openSmsHistoryDialog(item) {
      this.showSmsHistoryDialog = true
      this.selectedSimNumber = item?.number
      this.selectedSim = item
    },
    openRouterSummary(serial) {
      this.router.serialNumber = serial
      this.routerStore.openDialog(this.router)
    },
  },
}
</script>

<style>
.kit-tag {
  .tags__selected-items .tag__text {
    font-size: 11px;
  }
}
</style>
